import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Boxes, Loader2, Tag } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useViewer } from "@/components/viewer/useViewer";
import { FilterBar } from "@/components/table/FilterBar";
import { FactoryStatusPanel } from "@/components/factory/FactoryStatusPanel";
import { canEdit, useAuth } from "@/context/AuthContext";
import { FACTORY_STATUSES, FACTORY_STATUS_COLORS } from "@/constants/workflow";

/**
 * Factory model view — the uploaded model on the dark factory theme with the
 * same filter and label systems as the main model view. Factory-specific
 * statuses and properties will be layered on later.
 */
export default function FactoryModelPage() {
  const { projectId } = useParams();
  const containerRef = useRef(null);
  const [revisionId, setRevisionId] = useState(null);
  const [error, setError] = useState(null);
  const [panels, setPanels] = useState([]);
  const [options, setOptions] = useState({});
  const [filters, setFilters] = useState({});
  const [labelsOn, setLabelsOn] = useState(true);
  const [selection, setSelection] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [allPanels, setAllPanels] = useState([]);
  const { capabilities } = useAuth();
  // Factory statuses are owned by the production role (admin always allowed).
  const canEditFactory = canEdit(capabilities, "production");

  // Production may only start once Design is Approved AND BIM is Published.
  const isEligible = useCallback(
    (panel) =>
      panel.statuses?.design === "Approved" && panel.statuses?.bim === "Published",
    []
  );
  const lockedUids = useMemo(
    () => allPanels.filter((p) => !isEligible(p)).map((p) => p.panel_uid),
    [allPanels, isEligible]
  );
  // Legend counts mirror the Factory overview: Awaiting Cutting covers only
  // production-eligible panels (Design Approved + BIM Published).
  const stageCounts = useMemo(() => {
    const counts = {};
    allPanels.forEach((panel) => {
      const stage = panel.factory_status || "Awaiting Cutting";
      if (stage === "Awaiting Cutting" && !isEligible(panel)) return;
      counts[stage] = (counts[stage] || 0) + 1;
    });
    return counts;
  }, [allPanels, isEligible]);

  useEffect(() => {
    let alive = true;
    api
      .get(`/projects/${projectId}`)
      .then(({ data }) => {
        if (!alive) return;
        const project = data?.project || data;
        setRevisionId(project?.active_revision_id || null);
      })
      .catch((err) => {
        if (alive) setError(err.message || "Project load failed");
      });
    return () => {
      alive = false;
    };
  }, [projectId]);

  // Empty-area click clears the selection AND any active filters (Esc does the same).
  const filtersActiveRef = useRef(false);
  const handleSelect = useCallback((uids) => {
    if (uids.length === 0 && filtersActiveRef.current) setFilters({});
    setSelection(uids);
  }, []);

  const { viewer, ready, loadError, detailMode, detailProgress, detailAvailable, loadDetail, backToLight, clearDetailSelection } = useViewer({
    containerRef,
    projectId,
    revisionId,
    panels: allPanels,
    colorMode: "factory",
    onSelect: handleSelect,
    lockedUids,
    enabled: Boolean(revisionId),
    background: "#0A0F1A",
    // Factory keeps selected panels highlighted but never ghosts the rest.
    ghostUnselected: false,
    // Keep the built viewer alive across factory entries, like the main model view.
    cacheKey: revisionId ? `factory:${projectId}:${revisionId}` : null,
  });

  const query = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    return params.toString();
  }, [filters]);

  useEffect(() => {
    if (!revisionId) return undefined;
    let alive = true;
    api
      .get(`/projects/${projectId}/panels?${query}`)
      .then(({ data }) => {
        if (alive) setPanels(data.panels);
      })
      .catch((err) => {
        if (alive) toast.error(apiError(err));
      });
    return () => {
      alive = false;
    };
  }, [projectId, revisionId, query, refreshTick]);

  useEffect(() => {
    if (!revisionId) return;
    api
      .get(`/projects/${projectId}/panels/filter-options`)
      .then(({ data }) => setOptions(data))
      .catch(() => {});
  }, [projectId, revisionId]);

  // Eligibility comes from ALL panels, independent of the active filters.
  useEffect(() => {
    if (!revisionId) return;
    api
      .get(`/projects/${projectId}/panels`)
      .then(({ data }) => setAllPanels(data.panels))
      .catch(() => {});
  }, [projectId, revisionId, refreshTick]);

  // Same label system as the main model view.
  useEffect(() => {
    if (!viewer.current || !ready) return;
    viewer.current.setPanelLabels(
      labelsOn
        ? panels.map((panel) => ({
            uid: panel.panel_uid,
            text: panel.block_definition || panel.type_code,
            subtext: panel.unique_code || null,
          }))
        : []
    );
  }, [labelsOn, panels, ready, viewer]);

  // Same note badges as the main model view.
  useEffect(() => {
    if (!viewer.current || !ready || !revisionId) return;
    let cancelled = false;
    api
      .get(`/projects/${projectId}/panel-markers`)
      .then(({ data }) => {
        if (cancelled || !viewer.current) return;
        viewer.current.setNoteMarkers(data.notes);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId, revisionId, ready, viewer]);

  // Active filters isolate the matching panels; clearing them restores everything.
  const filtersActive = Object.values(filters).some(Boolean);
  filtersActiveRef.current = filtersActive;
  useEffect(() => {
    if (!viewer.current || !ready) return;
    if (filtersActive) viewer.current.isolate(panels.map((panel) => panel.panel_uid));
    else viewer.current.showAll();
  }, [filtersActive, panels, ready, viewer]);

  const selectFiltered = useCallback(() => {
    if (viewer.current && panels.length) {
      // Locked (not approved/published) panels can never be selected.
      const uids = panels.filter(isEligible).map((panel) => panel.panel_uid);
      viewer.current.setSelection(uids, false);
      setSelection(uids);
    }
  }, [panels, viewer, isEligible]);

  const clearSelection = useCallback(() => {
    viewer.current?.setSelection([], false);
    setSelection([]);
  }, [viewer]);

  // Esc clears all active filters and the panel selection (not while typing,
  // and not when a dropdown/menu is open — Esc then just closes it).
  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      const target = event.target;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      if (
        document.querySelector(
          '[data-radix-popper-content-wrapper], [role="menu"], [role="listbox"]'
        )
      ) {
        return;
      }
      setFilters({});
      clearSelection();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [clearSelection]);

  return (
    <div data-testid="factory-model-page" className="flex h-full flex-col">
      <FilterBar
        dark
        visibleFilters={["elevation", "level", "type_code", "panel_type"]}
        filters={filters}
        setFilters={setFilters}
        options={options}
        onReset={() => setFilters({})}
        onSelectFiltered={selectFiltered}
        matchCount={panels.filter(isEligible).length}
        presets={[]}
        onSavePreset={() => {}}
        onDeletePreset={() => {}}
        canManagePresets={false}
      />

      <div className="relative min-h-0 flex-1">
        <div ref={containerRef} className="absolute inset-0" data-testid="factory-viewer-canvas" />

        <div
          data-testid="factory-status-legend"
          className="absolute bottom-4 right-4 z-10 rounded-xl border border-white/10 bg-[#0D1526]/90 px-4 py-3.5 backdrop-blur-md"
        >
          <p className="mono mb-2.5 text-[10.5px] uppercase tracking-[0.22em] text-slate-400">
            Factory Status
          </p>
          <div className="space-y-1.5">
            {FACTORY_STATUSES.map((stage) => (
              <div key={stage} className="flex items-center gap-2.5">
                <span
                  data-testid={`legend-dot-${stage.replace(/\s/g, "-")}`}
                  className={`h-3 w-3 shrink-0 rounded-full ${
                    FACTORY_STATUS_COLORS[stage] ? "" : "border border-white/30"
                  }`}
                  style={{ backgroundColor: FACTORY_STATUS_COLORS[stage] || "transparent" }}
                />
                <span className="text-[12.5px] text-slate-200">{stage}</span>
                <span
                  data-testid={`legend-count-${stage.replace(/\s/g, "-")}`}
                  className="mono ml-auto pl-4 text-[11.5px] text-slate-400"
                >
                  {stageCounts[stage] || 0}
                </span>
              </div>
            ))}
          </div>
        </div>

        {selection.length > 0 && canEditFactory ? (
          <FactoryStatusPanel
            projectId={projectId}
            selection={selection}
            panels={allPanels}
            onDone={() => setRefreshTick((t) => t + 1)}
            onClose={clearSelection}
          />
        ) : null}

        <div className="absolute right-4 top-4 z-20 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            data-testid="factory-toggle-labels"
            onClick={() => setLabelsOn((v) => !v)}
            className={`mono flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-wider transition-colors ${
              labelsOn
                ? "border-cyan-400/50 bg-cyan-500/15 text-cyan-300"
                : "border-white/15 bg-white/5 text-slate-400 hover:bg-white/10"
            }`}
          >
            <Tag size={12} /> Labels
          </button>

          {detailAvailable ? (
            <>
              <button
                type="button"
                data-testid="factory-toggle-detail"
                disabled={!!detailProgress}
                onClick={() => (detailMode === "full" ? backToLight() : loadDetail(null))}
                className={`mono flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-wider transition-colors disabled:opacity-50 ${
                  detailMode !== "light"
                    ? "border-cyan-400/50 bg-cyan-500/15 text-cyan-300"
                    : "border-white/15 bg-white/5 text-slate-400 hover:bg-white/10"
                }`}
              >
                <Boxes size={12} /> Detailed model
              </button>

              {selection.length > 0 ? (
                <button
                  type="button"
                  data-testid="factory-detail-selection"
                  disabled={!!detailProgress}
                  onClick={() => loadDetail(selection)}
                  className="mono flex items-center gap-1.5 rounded-full border border-cyan-400/40 bg-white/5 px-3 py-1.5 text-[10px] uppercase tracking-wider text-cyan-300 hover:bg-white/10 disabled:opacity-50"
                >
                  <Boxes size={12} /> Detail selected ({selection.length})
                </button>
              ) : null}

              {detailMode === "selected" ? (
                <button
                  type="button"
                  data-testid="factory-detail-clear"
                  onClick={clearDetailSelection}
                  title="Return detailed panels to their boxes"
                  className="mono flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 transition-colors hover:bg-white/10 hover:text-slate-200"
                >
                  Clear
                </button>
              ) : null}

              {detailProgress ? (
                <span
                  data-testid="factory-detail-progress"
                  className="mono flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1.5 text-[10px] uppercase tracking-wider text-cyan-300"
                >
                  <Loader2 size={12} className="animate-spin" />
                  {detailProgress.loaded}/{detailProgress.total}
                </span>
              ) : null}
            </>
          ) : null}
        </div>

        {!ready && !loadError && !error ? (
          <div
            data-testid="factory-model-loading"
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <span className="mono flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-cyan-300/80">
              <Loader2 size={14} className="animate-spin" />
              Loading model…
            </span>
          </div>
        ) : null}

        {loadError || error ? (
          <div
            data-testid="factory-model-error"
            className="absolute inset-0 flex items-center justify-center"
          >
            <p className="text-[12px] text-red-400">{loadError || error}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
